/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { RootState } from '../../../store';
import {
    ICartItemInfo,
    ISession,
    IUpdateItemQuantityRequest,
} from '../../../models/pro-sites';
import CartHeader from './cart-header';
import CartItem from './cart-item';
import {
    loadSavedCart,
    movePrimaryToSaved,
    moveSavedToPrimary,
    updateQuantity,
    clear,
} from '../../../store/cart/cart-actions';
import {
    loadSession,
    loadSessionSilent,
} from '../../../store/session/session-actions';
import {
    cartItemType,
    CartType,
    HEARING_AIDS_URL_PARAMS,
} from '../../../models/constants';
import { setIsBusy } from '../../../store/api/api-status-action';
import ClearModal from './clear-modal';
import DeleteModal from './delete-modal';
import MoveToCartModal from './move-to-cart-modal';
import { Link } from 'react-router-dom';
import * as cartApi from '../../../api/cart-api';
import { useCookies } from 'react-cookie';
import { Navigation } from '../../../global-functions';
import {
    BATTERY_PRODUCT_TYPE,
    BS,
    CHARGER_OPTIONS_ID_LIST,
    DE,
    POWER_ADAPTER_PART_NUMBER,
    noChargerValidationCountries,
} from '../../common/constants';
import { usePiTracker } from '../../../hooks/pi-tracker';
import Alert from '../../common/alert';
interface ICartProps {
    cart: ICartItemInfo[];
    session: ISession;
    savedCart: ICartItemInfo[] | null;
    isPunchout: boolean;
    loadSession: () => Promise<void>;
    loadSessionSilent: () => Promise<void>;
    loadSavedCart: () => Promise<void>;
    movePrimaryToSaved: (lineItemId: string) => Promise<void>;
    moveSavedToPrimary: (lineItemId: string) => Promise<void>;
    updateQuantity: (request: IUpdateItemQuantityRequest) => Promise<void>;
    clear: (cartType: CartType) => Promise<void>;
    setIsBusy: (isBusy: boolean) => Promise<void>;
    apiCallsInProgress: number;
}

export const Cart = ({
    cart,
    session,
    savedCart,
    isPunchout,
    loadSession,
    loadSessionSilent,
    loadSavedCart,
    movePrimaryToSaved,
    moveSavedToPrimary,
    updateQuantity,
    clear,
    setIsBusy,
    apiCallsInProgress,
}: ICartProps) => {
    const [showClearModal, setShowClearModal] = useState(false);
    const [clearingSaved, setClearingSaved] = useState(false);
    const [clearFunc, setClearFunc] = useState(() => () => {});

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteFunc, setDeleteFunc] = useState(() => () => {});
    const [currentItem, setCurrentItem] = useState<ICartItemInfo | null>(null);

    const [showMoveToCartModal, setShowMoveToCartModal] = useState(false);
    const [moveToCartFunc, setMoveToCartFunc] = useState(() => () => {});

    const [cookies] = useCookies(['punchout']);

    const [chargersQuantityError, setChargersQuantityError] = useState(false);
    const [isChargersQuantityExceeded, setIsChargersQuantityExceeded] =
        useState(false);
    const [powerAdapterQuantityError, setPowerAdapterQuantityError] =
        useState(false);
    const [disableProceedToCheckout, setDisableProceedToCheckout] =
        useState(false);
    const [cartType, setCartType] = useState<CartType>(CartType.Primary);
    const [cartMessages, setCartMessages] = useState<string[] | null>(null);
    const intl = useIntl();
    const disableCheckoutButton =
        disableProceedToCheckout ||
        chargersQuantityError ||
        powerAdapterQuantityError;

    const getCartMaxQuantity = (item: ICartItemInfo) => {
        if (session) {
            if (session.isSiteUpgraded && item.cartItemTypeId === 55) {
                return 150;
            }
            if (
                session.isSiteUpgraded &&
                item.cartItemTypeId === 50 &&
                item.partType === 2
            ) {
                return 1000000;
            }
        }
        return 500;
    };

    useEffect(() => {
        if (!savedCart) {
            // Note: Sometimes the isBusy indicator goes away too soon which is why
            //       I added a call to it below (this was noticed when entering the
            //       spa and may not be necessary once the entire site is in the spa).
            //       Also when chaining two actions together (as is done in some of the
            //       functions below) the isBusy indicator goes away too soon and as I
            //       did here I added a call to setIsBusy.
            setIsBusy(true);
            loadSavedCart().then(() => {
                setIsBusy(false);
            });
        }
    }, []);

    usePiTracker();

    useEffect(() => {
        checkChargersQuantity();
        checkPowerAdapterQuantity();
        setCartMessages([
            ...new Set(
                cart
                    .map((cartItem) => cartItem.cartMessage)
                    .filter((message) => message)
            ),
        ]);
    }, [cart]);

    const handleClear = (cartType: CartType) => {
        if (cartType === CartType.Saved) {
            setClearingSaved(true);
        }
        setShowClearModal(true);
        setClearFunc(() => () => {
            setShowClearModal(false);
            setClearingSaved(false);
            clear(cartType);
        });
    };

    const handleDeleteItem = (
        item: ICartItemInfo,
        quantity: number,
        cartType: CartType
    ) => {
        setCurrentItem(item);
        setShowDeleteModal(true);
        setDeleteFunc(() => () => {
            setShowDeleteModal(false);
            setIsBusy(true);
            updateQuantity({
                itemId: item.itemID,
                quantity: quantity,
            } as IUpdateItemQuantityRequest).then(() => {
                if (cartType === CartType.Primary) {
                    setCartType(CartType.Primary);
                    loadSession().then(() => {
                        setCurrentItem(null);
                        setIsBusy(false);
                    });
                } else {
                    setCartType(CartType.Saved);
                    loadSavedCart().then(() => {
                        setCurrentItem(null);
                        setIsBusy(false);
                    });
                }
            });
        });
    };

    const handleMovePrimaryToSaved = (lineItemId: string) => {
        setIsBusy(true);
        movePrimaryToSaved(lineItemId).then(() => {
            loadSession().then(() => {
                setIsBusy(false);
            });
        });
    };

    const handleMoveSavedToPrimary = (
        lineItemId: string,
        isPatient: boolean
    ) => {
        if ((cart.length > 0 && cart[0].isPatient) || isPatient) {
            setShowMoveToCartModal(true);
            setMoveToCartFunc(() => () => {
                setShowMoveToCartModal(false);
                setIsBusy(true);
                moveSavedToPrimary(lineItemId).then(() => {
                    loadSession().then(() => {
                        setIsBusy(false);
                    });
                });
            });
        } else {
            setIsBusy(true);
            moveSavedToPrimary(lineItemId).then(() => {
                loadSession().then(() => {
                    setIsBusy(false);
                });
            });
        }
    };

    const checkChargersQuantity = () => {
        if (cartType === CartType.Primary && cart && cart.length > 0) {
            const totalHearingAidQuantity = cart.reduce((acc, cartItem) => {
                if (
                    cartItem.productOptions &&
                    cartItem.cartItemTypeId &&
                    [
                        cartItemType.RicWithStockReceiver,
                        cartItemType.BteOnly,
                    ].includes(cartItem.cartItemTypeId) &&
                    cartItem.productOptions.some((option) =>
                        CHARGER_OPTIONS_ID_LIST.includes(option.id)
                    )
                ) {
                    acc = acc + +cartItem.quantity;
                }
                return acc;
            }, 0);
            const filterChargerAccessories = cart.filter(
                (cartItem) => cartItem.productType === BATTERY_PRODUCT_TYPE
            );
            const totalChargersQuantity = filterChargerAccessories!.reduce(
                (acc, charger) => acc + +charger.quantity,
                0
            );
            const chargersQuantity = Math.round(totalHearingAidQuantity / 2);

            if (
                !noChargerValidationCountries.includes(session!.country) &&
                totalHearingAidQuantity > 0 &&
                totalChargersQuantity !== chargersQuantity
            ) {
                if (totalChargersQuantity > chargersQuantity) {
                    setIsChargersQuantityExceeded(true);
                } else {
                    setIsChargersQuantityExceeded(false);
                }
                setChargersQuantityError(true);
            } else {
                setChargersQuantityError(false);
            }
        }
    };

    const checkPowerAdapterQuantity = () => {
        if (
            session.country === DE &&
            cartType === CartType.Primary &&
            cart &&
            cart.length > 0
        ) {
            const totaPowerAdapterQuantity = cart.reduce((acc, cartItem) => {
                if (cartItem.partNumber === POWER_ADAPTER_PART_NUMBER) {
                    acc = acc + +cartItem.quantity;
                }
                return acc;
            }, 0);
            if (totaPowerAdapterQuantity > 10) {
                setPowerAdapterQuantityError(true);
            } else {
                setPowerAdapterQuantityError(false);
            }
        }
    };

    const handleUpdateQuantity = async (
        itemId: string,
        quantity: number,
        cartType: CartType
    ) => {
        await updateQuantity({ itemId: itemId, quantity: quantity });
        if (cartType === CartType.Primary) {
            setCartType(CartType.Primary);
            await loadSessionSilent();
        } else {
            setCartType(CartType.Saved);
            await loadSavedCart();
        }
        setDisableProceedToCheckout(false);
    };

    const handleProceedToCheckout = () => {
        while (apiCallsInProgress <= 0) {
            Navigation.pushExternal('/estore/checkout');
            break;
        }
    };

    const handlePunchout = () => {
        (async () => {
            setIsBusy(true);
            cartApi
                .punchout()
                .then((result) => {
                    const form = document.createElement('form');
                    form.id = 'punchout';
                    form.method = 'POST';
                    form.target = '_top';
                    form.action = cookies.punchout;
                    result.data!.forEach((item) => {
                        const element = document.createElement('input');
                        element.type = 'hidden';
                        element.id = item[0];
                        element.name = item[0];
                        element.value = item[1];
                        form.appendChild(element);
                    });
                    document.body.appendChild(form);
                    form.submit();
                    form.remove();
                    setIsBusy(false);
                })
                .catch(() => {
                    setIsBusy(false);
                });
        })();
    };

    return (
        <div id="wrapper">
            <div>
                <h1>
                    <FormattedMessage id="Shopping_Cart" />
                </h1>
            </div>
            <div>
                {cart.length > 0 ? (
                    <>
                        <div
                            className="row"
                            style={{
                                margin: '0px 0px',
                            }}
                        >
                            <div
                                className="col-md-6"
                                style={{ paddingLeft: '0px' }}
                            >
                                <button
                                    type="button"
                                    className="btn btn-default"
                                    onClick={() => {
                                        handleClear(CartType.Primary);
                                    }}
                                >
                                    <FormattedMessage id="Clear_All_Cart" />
                                </button>
                            </div>
                            <div
                                className="col-md-6"
                                style={{ paddingRight: '0px' }}
                            >
                                <div className="pull-right">
                                    <Link
                                        to={
                                            '/estore/product-list?' +
                                            HEARING_AIDS_URL_PARAMS
                                        }
                                        className="btn btn-default"
                                    >
                                        <FormattedMessage id="Continue_Shopping" />
                                    </Link>
                                    {isPunchout ? (
                                        <button
                                            id="proceed-to-punchout"
                                            type="button"
                                            className="btn btn-success"
                                            onClick={handlePunchout}
                                            disabled={disableCheckoutButton}
                                        >
                                            <FormattedMessage id="Send_to_OneLink" />
                                        </button>
                                    ) : (
                                        <button
                                            id="Proceed_to_Checkout1"
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleProceedToCheckout}
                                            disabled={disableCheckoutButton}
                                        >
                                            <FormattedMessage id="Proceed_to_Checkout" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                        {cartMessages &&
                            cartMessages.map((message, i) => (
                                <Alert
                                    key={i}
                                    message={message}
                                    type={BS.Info}
                                />
                            ))}
                        {chargersQuantityError &&
                            (isChargersQuantityExceeded ? (
                                <Alert
                                    message={
                                        'Maximum number of chargers per order exceeded'
                                    }
                                    type={BS.Danger}
                                    translationId={'Max_Chargers'}
                                />
                            ) : (
                                <Alert
                                    message={
                                        'Must select maximum number of chargers to continue'
                                    }
                                    type={BS.Danger}
                                    translationId={'Min_Chargers'}
                                />
                            ))}
                        {powerAdapterQuantityError && (
                            <Alert
                                message={
                                    'Maximum quantity of 10 Power Adapters per order exceeded'
                                }
                                type={BS.Danger}
                                translationId={'Max_Power_Adapter'}
                            />
                        )}

                        <CartHeader />
                        {cart.map((item, i) => (
                            <CartItem
                                key={i}
                                item={item}
                                session={session}
                                maxQuantity={getCartMaxQuantity(item)}
                                type={CartType.Primary}
                                addTopLine={false}
                                addBottomLine={false}
                                onMoveItem={handleMovePrimaryToSaved}
                                onDeleteItem={() => {
                                    handleDeleteItem(item, 0, CartType.Primary);
                                }}
                                onUpdateItemQuantity={async (
                                    itemId: string,
                                    quantity: number
                                ) => {
                                    await handleUpdateQuantity(
                                        itemId,
                                        quantity,
                                        CartType.Primary
                                    );
                                }}
                                setDisableProceedToCheckout={
                                    setDisableProceedToCheckout
                                }
                            />
                        ))}
                        <div className="row" style={{ paddingTop: '20px' }}>
                            <div className="col-md-8">
                                <img
                                    alt="Secure Checkout"
                                    style={{
                                        marginTop: '6px',
                                        marginRight: '5px',
                                    }}
                                    src="/images/icons/lock.png"
                                    className="pull-left"
                                />
                                <span>
                                    <span style={{ fontWeight: 'bold' }}>
                                        <FormattedMessage id="Secure_Checkout" />
                                    </span>
                                    <br />
                                    <span>
                                        <FormattedMessage id="Shopping_is_always_safe_and_secure" />
                                    </span>
                                </span>
                            </div>
                            <div className="col-md-4">
                                <div className="pull-right">
                                    {isPunchout ? (
                                        <button
                                            id="proceed-to-punchout"
                                            type="button"
                                            className="btn btn-success"
                                            onClick={handlePunchout}
                                            disabled={disableCheckoutButton}
                                        >
                                            <FormattedMessage id="Send_to_OneLink" />
                                        </button>
                                    ) : (
                                        <button
                                            id="Proceed_to_Checkout2"
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleProceedToCheckout}
                                            disabled={disableCheckoutButton}
                                        >
                                            <FormattedMessage id="Proceed_to_Checkout" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="row">
                            <div
                                className="col-md-8"
                                style={{
                                    fontSize: 'larger',
                                    fontWeight: 'bold',
                                    paddingBottom: '10px',
                                }}
                            >
                                <FormattedMessage id="Your_cart_is_empty" />
                            </div>
                            <div className="col-md-4">
                                <div className="pull-right">
                                    <Link
                                        to={
                                            '/estore/product-list?' +
                                            HEARING_AIDS_URL_PARAMS
                                        }
                                        className="btn btn-default"
                                    >
                                        <FormattedMessage id="Continue_Shopping" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                        {savedCart && (
                            <div className="row">
                                <div className="col-md-12">
                                    <FormattedMessage id="To_check_out_move_saved_items" />
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
            {savedCart && savedCart.length > 0 && (
                <div>
                    <CartHeader
                        firstColumnText={
                            intl.formatMessage({
                                id: 'Saved_For_Later',
                            }) +
                            ' (' +
                            savedCart
                                .map((item) => item.quantity)
                                .reduce((a, b) => {
                                    return Number(a) + Number(b);
                                }) +
                            ')'
                        }
                    />
                    <button
                        type="button"
                        className="btn btn-default"
                        onClick={() => {
                            handleClear(CartType.Saved);
                        }}
                    >
                        <FormattedMessage
                            id="Clear_All_Saved_Cart"
                            defaultMessage="Clear all saved cart"
                        />
                    </button>
                    {savedCart.map((item, i) => (
                        <CartItem
                            key={i}
                            session={session}
                            item={item}
                            maxQuantity={getCartMaxQuantity(item)}
                            type={CartType.Saved}
                            addTopLine={
                                item.itemID === item.lineItemID &&
                                item.itemID !== savedCart[0].itemID
                            }
                            addBottomLine={
                                item.itemID ===
                                savedCart[savedCart.length - 1].itemID
                            }
                            onMoveItem={handleMoveSavedToPrimary}
                            onDeleteItem={() => {
                                handleDeleteItem(item, 0, CartType.Saved);
                            }}
                            onUpdateItemQuantity={async (
                                itemId: string,
                                quantity: number
                            ) => {
                                await handleUpdateQuantity(
                                    itemId,
                                    quantity,
                                    CartType.Saved
                                );
                            }}
                            setDisableProceedToCheckout={
                                setDisableProceedToCheckout
                            }
                        />
                    ))}
                </div>
            )}
            {showClearModal && (
                <ClearModal
                    isSaved={clearingSaved}
                    onAccept={clearFunc}
                    onClose={() => {
                        setShowClearModal(false);
                        setClearingSaved(false);
                    }}
                />
            )}
            {showDeleteModal && (
                <DeleteModal
                    item={currentItem!}
                    onAccept={deleteFunc}
                    onClose={() => {
                        setShowDeleteModal(false);
                    }}
                />
            )}
            {showMoveToCartModal && (
                <MoveToCartModal
                    onAccept={moveToCartFunc}
                    onClose={() => {
                        setShowMoveToCartModal(false);
                    }}
                />
            )}
        </div>
    );
};

const mapStateToProps = (state: RootState) => ({
    cart: state.session!.data!.carts.primary,
    savedCart: state.savedCart ? state.savedCart.data! : null,
    isPunchout: state.session!.data!.siteNumber === 44,
    apiCallsInProgress: state.apiCallsInProgress,
    session: state.session!.data!,
});

const mapDispatchToProps = {
    loadSession,
    loadSessionSilent,
    loadSavedCart,
    movePrimaryToSaved,
    moveSavedToPrimary,
    updateQuantity,
    clear,
    setIsBusy,
};

const cartContainer = connect(mapStateToProps, mapDispatchToProps)(Cart);

export default cartContainer;
